class PDFExportService {
  constructor() {
    this.colors = {
      background: "#F7F5EE",
      border: "#BDBAB4",
      headerBackground: "#E9E7E0",
      text: "#000000",
      configText: "#333333",
      configBg: "#FDFCFA",
    };
    this.fontSize = 11;
    this.pageMargin = 15;
    this.pageWidth = 210;
  }

  async exportQuote(quote) {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);

    const pdf = new jsPDF("p", "mm", "a4");

    this.setupFonts(pdf);

    this.setColor(pdf, this.colors.background, "fill");
    pdf.rect(
      0,
      0,
      pdf.internal.pageSize.width,
      pdf.internal.pageSize.height,
      "F"
    );

    let yPosition = 15;

    yPosition = await this.addLogo(pdf, yPosition);
    yPosition = this.addQuoteInfo(pdf, quote, yPosition);
    await this.addProductsTable(pdf, quote, yPosition, autoTable);

    const fileName = `quote-${quote.name
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")}.pdf`;
    pdf.save(fileName);
  }

  // ---------------------------------------------------------------------------
  // Sales Order invoice PDF (mirrors the EBMS "Sales Order" report). Driven by
  // the /orders/{autoid}/invoice payload: { header, items } where items is the
  // printable tree (root products + their PRINT=true configuration components).
  // ---------------------------------------------------------------------------

  get company() {
    return {
      name: "Dundalk Leisure Craft Inc.",
      lines: ["318448 8th Line NE", "Melancthon, ON L9V 2K3"],
      phone: "888-923-9813",
      email: "info@leisurecraft.com",
      hst: "HST/GST # 834645103 RT0001",
      // Home currency: what the ERP books orders in before converting them to
      // the customer's currency.
      currency: "CAD",
    };
  }

  async exportInvoice(invoice) {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);

    const header = invoice.header || {};
    const items = Array.isArray(invoice.items) ? invoice.items : [];
    const payments = Array.isArray(invoice.payments) ? invoice.payments : [];
    // Negative total => EBMS credit invoice; switches title, labels and adds
    // the "Current Payments Applied to Invoice" table.
    const isCredit = Number(header.total) < 0;

    const pdf = new jsPDF("p", "mm", "a4");
    this.setupFonts(pdf);

    let yPosition = 15;
    yPosition = await this.addInvoiceMasthead(pdf, header, yPosition, isCredit);
    yPosition = this.addInvoiceAddresses(pdf, header, yPosition);
    yPosition = this.addInvoiceInfoBar(pdf, header, yPosition, autoTable);
    yPosition = this.addInvoiceCalcNote(pdf, header, yPosition);
    yPosition = this.addInvoiceItems(pdf, items, yPosition, autoTable);
    if (isCredit && payments.length) {
      yPosition = this.addInvoicePayments(pdf, payments, yPosition);
    }
    this.addInvoiceTotals(pdf, header, isCredit, yPosition);
    this.addInvoiceFooter(pdf, header);

    const docName = isCredit ? "credit-invoice" : "sales-order";
    const orderNo = String(header.invoice || "order").replace(/[^\w-]/g, "");
    pdf.save(`${docName}-${orderNo}.pdf`);
  }

  async addInvoiceMasthead(pdf, header, yPosition, isCredit) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const center = pdf.internal.pageSize.width / 2;
    const startY = yPosition;

    // Company name, logo and "Sales Order" share one band and are vertically
    // centered on it (bandCenter); the "Order Entered" line stays at the very
    // top-right. Company address lines flow below the band.
    const bandCenter = startY + 8;

    // Company name — centered on the band, address below it
    let leftY = bandCenter + 1.5;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text(this.company.name, left, leftY);
    leftY += 5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    this.setColor(pdf, this.colors.configText, "text");
    const companyMeta = [
      ...this.company.lines,
      `Phone: ${this.company.phone}`,
      this.company.email,
      this.company.hst,
    ];
    companyMeta.forEach((line) => {
      pdf.text(line, left, leftY);
      leftY += 4;
    });

    // Logo (center of the band, vertically centered)
    try {
      const logo = await this.loadLogoAsBase64("/icons/logo-sm.svg");
      const w = 40;
      const h = (34 / 203) * w;
      pdf.addImage(logo, "PNG", center - w / 2, bandCenter - h / 2, w, h);
    } catch (e) {
      /* logo is optional */
    }

    // "Order Entered <date> by <user>" (very top-right), date + user bold
    if (header.creaDate || header.userName) {
      const enteredDate = header.creaDate
        ? this.formatDateUS(header.creaDate)
        : "";
      const enteredBy = this.cleanText(header.userName || "");
      const segments = [{ text: "Order Entered", bold: false }];
      if (enteredDate) segments.push({ text: enteredDate, bold: true });
      if (enteredBy) {
        segments.push({ text: "by", bold: false });
        segments.push({ text: enteredBy, bold: true });
      }

      pdf.setFontSize(8);
      this.setColor(pdf, this.colors.configText, "text");
      this.drawRightAlignedSegments(pdf, segments, right, startY);
    }

    // Title (right of the band, vertically centered) — "Sales Order" normally,
    // "Credit Invoice" for credit invoices.
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(isCredit ? 20 : 22);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text(isCredit ? "Credit Invoice" : "Sales Order", right, bandCenter + 2.8, {
      align: "right",
    });

    // Order Number / Date box (under the band)
    const boxY = startY + 16;
    const boxH = 12;
    const boxW = 80;
    const boxX = right - boxW;
    const colW = boxW / 2;

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.rect(boxX, boxY, boxW, boxH);
    pdf.line(boxX + colW, boxY, boxX + colW, boxY + boxH);
    pdf.line(boxX, boxY + 5, boxX + boxW, boxY + 5);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    pdf.text(isCredit ? "Invoice Number" : "Order Number", boxX + colW / 2, boxY + 3.5, {
      align: "center",
    });
    pdf.text("Date", boxX + colW + colW / 2, boxY + 3.5, { align: "center" });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text(String(header.invoice || "-"), boxX + colW / 2, boxY + 9.5, {
      align: "center",
    });
    pdf.text(this.formatDateUS(new Date()), boxX + colW + colW / 2, boxY + 9.5, {
      align: "center",
    });

    return Math.max(leftY, boxY + boxH) + 6;
  }

  addInvoiceAddresses(pdf, header, yPosition) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const colRight = left + (right - left) / 2 + 5;

    const billLines = this.addressLines({
      name: header.name,
      address1: header.address1,
      address2: header.address2,
      city: header.city,
      state: header.state,
      zip: header.zip,
      country: header.country,
      phone: header.phone,
      email: header.email,
    });
    const shipLines = this.addressLines({
      name: header.cName,
      address1: header.cAddress1,
      address2: header.cAddress2,
      city: header.cCity,
      state: header.cState,
      zip: header.cZip,
      country: header.cCountry,
      phone: header.cPhone,
      email: header.cEmail,
    });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("Bill To", left, yPosition);
    pdf.text("Ship To", colRight, yPosition);

    let lineY = yPosition + 5;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.configText, "text");
    const rows = Math.max(billLines.length, shipLines.length);
    for (let i = 0; i < rows; i++) {
      if (billLines[i]) pdf.text(billLines[i], left, lineY);
      if (shipLines[i]) pdf.text(shipLines[i], colRight, lineY);
      lineY += 4.5;
    }

    return lineY + 4;
  }

  addressLines(a) {
    const lines = [];
    if (!this.isFieldEmpty(a.name)) lines.push(a.name);
    if (!this.isFieldEmpty(a.address1)) lines.push(a.address1);
    if (!this.isFieldEmpty(a.address2)) lines.push(a.address2);
    const cityLine = [a.city, a.state, a.zip]
      .filter((p) => !this.isFieldEmpty(p))
      .join(", ");
    if (cityLine) lines.push(cityLine);
    if (!this.isFieldEmpty(a.country)) lines.push(a.country);
    if (!this.isFieldEmpty(a.phone)) lines.push(`Phone: ${a.phone}`);
    if (!this.isFieldEmpty(a.email)) lines.push(`E-Mail: ${a.email}`);
    return lines;
  }

  addInvoiceInfoBar(pdf, header, yPosition, autoTable) {
    const headerRgb = this.hexToRgb(this.colors.headerBackground);
    const borderRgb = this.hexToRgb(this.colors.border);
    const textRgb = this.hexToRgb(this.colors.text);

    autoTable(pdf, {
      startY: yPosition,
      head: [
        [
          "Customer ID",
          "Sales Person",
          "P.O. Number",
          "Estimated Ship Date",
          "Ship Via",
          "Terms",
        ],
      ],
      body: [
        [
          header.cId || "-",
          header.salesman || "-",
          header.poNo || "-",
          header.shipDate ? this.formatDateUS(header.shipDate) : "-",
          header.shipVia || "-",
          header.terms || "-",
        ],
      ],
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        halign: "center",
        valign: "middle",
        lineColor: [borderRgb.r, borderRgb.g, borderRgb.b],
        lineWidth: 0.2,
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        font: "helvetica",
      },
      headStyles: {
        fillColor: [headerRgb.r, headerRgb.g, headerRgb.b],
        fontStyle: "bold",
        textColor: [textRgb.r, textRgb.g, textRgb.b],
      },
      margin: { left: this.pageMargin, right: this.pageMargin },
    });

    return pdf.lastAutoTable.finalY + 4;
  }

  // How to read the numbers below — printed above the items so it is seen before
  // the amounts are. Two things confuse customers otherwise: an order priced in
  // their currency but booked by the ERP in ours, and a back-ordered line, which
  // is listed here (it was ordered) but invoiced on the back-order document.
  addInvoiceCalcNote(pdf, header, yPosition) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const currency = this.cleanText(header.currency || "");
    const rate = Number(header.currencyRate);
    const converted = !!currency && currency !== this.company.currency && rate > 0 && rate !== 1;

    const notes = [];
    if (currency) {
      notes.push(
        converted
          ? `All amounts are shown in ${currency}, converted by our ERP from ${
              this.company.currency
            } at ${rate.toFixed(4)}. Converted amounts are rounded line by line, so the lines can differ from the Subtotal by a few cents.`
          : `All amounts are shown in ${currency}.`
      );
    }
    notes.push(
      "Extended is what the line is invoiced on this document, so it follows the Shipped quantity, not the Ordered one. Anything still on back order is listed here with Extended 0.00 and is charged on its own back-order document."
    );

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(7.5);
    this.setColor(pdf, this.colors.configText, "text");
    let y = yPosition;
    notes.forEach((note) => {
      const lines = pdf.splitTextToSize(note, right - left);
      pdf.text(lines, left, y);
      y += lines.length * 3.4;
    });

    return y + 3;
  }

  addInvoiceItems(pdf, items, yPosition, autoTable) {
    const headerRgb = this.hexToRgb(this.colors.headerBackground);
    const borderRgb = this.hexToRgb(this.colors.border);
    const textRgb = this.hexToRgb(this.colors.text);

    const body = items.map((entry) => {
      const item = entry.item || {};
      const components = Array.isArray(entry.components) ? entry.components : [];

      let description = this.cleanText(item.DESCR || item.INVEN || "");
      components.forEach((c) => {
        const descr = this.cleanText(c.DESCR || c.INVEN || "");
        if (!descr) return;
        // Only prefix the quantity when the line is flagged to print it
        // (PRINT_QUAN) — matches EBMS, which omits it on some configurations.
        const qty = this.formatQty(c.M_QUAN_VIS);
        const prefix = c.PRINT_QUAN && qty ? `${qty}) ` : "";
        description += `\n${prefix}${descr}`;
      });

      return [
        this.formatQty(item.M_QUAN_VIS),
        this.formatQty(item.SHIP),
        this.cleanText(item.UNIT_MEAS || ""),
        this.formatQty(item.WEIGHT),
        this.cleanText(item.INVEN || ""),
        description,
        this.formatNumber(item.UNIT),
        this.formatNumber(item.SO_AMOUNT),
      ];
    });

    if (!body.length) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      this.setColor(pdf, this.colors.configText, "text");
      pdf.text("No products found", this.pageMargin, yPosition + 4);
      return yPosition + 10;
    }

    autoTable(pdf, {
      startY: yPosition,
      head: [
        [
          "Ordered",
          "Shipped",
          "Unit",
          "Weight",
          "Product ID",
          "Description",
          "Unit Price",
          "Extended",
        ],
      ],
      body,
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 2,
        valign: "top",
        overflow: "linebreak",
        lineColor: [borderRgb.r, borderRgb.g, borderRgb.b],
        lineWidth: 0.2,
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        font: "helvetica",
      },
      headStyles: {
        fillColor: [headerRgb.r, headerRgb.g, headerRgb.b],
        fontStyle: "bold",
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 15, halign: "center" },
        1: { cellWidth: 15, halign: "center" },
        2: { cellWidth: 12, halign: "center" },
        3: { cellWidth: 15, halign: "center" },
        4: { cellWidth: 22 },
        5: { cellWidth: "auto" },
        6: { cellWidth: 21, halign: "right" },
        7: { cellWidth: 21, halign: "right" },
      },
      margin: { left: this.pageMargin, right: this.pageMargin },
    });

    return pdf.lastAutoTable.finalY + 6;
  }

  // "Current Payments Applied to Invoice" — a small centered table rendered
  // manually (Date · Reference · Amt). Only shown on credit invoices.
  addInvoicePayments(pdf, payments, yPosition) {
    const center = pdf.internal.pageSize.width / 2;
    const tableW = 110;
    const x0 = center - tableW / 2;
    const x1 = center + tableW / 2;
    const dateX = x0 + 2;
    const refX = center + 2;
    const amtX = x1 - 2;

    let y = yPosition + 4;
    const pageHeight = pdf.internal.pageSize.height;
    if (y + 14 + payments.length * 5 > pageHeight - this.pageMargin) {
      pdf.addPage();
      y = this.pageMargin + 10;
    }

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.line(x0, y, x1, y);
    y += 5;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("Current Payments Applied to Invoice", center, y, { align: "center" });
    y += 5;

    pdf.setFontSize(8);
    pdf.text("Date", dateX, y);
    pdf.text("Reference", refX, y, { align: "center" });
    pdf.text("Amt", amtX, y, { align: "right" });
    y += 1.5;
    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.2);
    pdf.line(x0, y, x1, y);
    y += 4.5;

    pdf.setFont("helvetica", "normal");
    this.setColor(pdf, this.colors.text, "text");
    payments.forEach((p) => {
      pdf.text(p.date ? this.formatDateUS(p.date) : "", dateX, y);
      pdf.text(this.paymentReference(p), refX, y, { align: "center" });
      pdf.text(`$${this.formatNumber(p.amount)}`, amtX, y, { align: "right" });
      y += 5;
    });

    return y + 2;
  }

  paymentReference(p) {
    const ck = this.cleanText((p && p.checkNo) || "");
    return ck ? `Ck #${ck}` : "Ck #";
  }

  addInvoiceTotals(pdf, header, isCredit, startY) {
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const labelX = right - 60;
    let y = (startY != null ? startY : pdf.lastAutoTable.finalY) + 8;

    const pageHeight = pdf.internal.pageSize.height;
    if (y + 45 > pageHeight - this.pageMargin) {
      pdf.addPage();
      y = this.pageMargin + 10;
    }

    const rows = [
      ["Subtotal", this.formatNumber(header.subtotal), false],
      ["Freight", this.formatNumber(header.freight), false],
      ["GST/HST", this.formatNumber(header.tax), false],
      ["TOTAL", this.formatNumber(header.total), true],
    ];

    pdf.setFontSize(10);
    this.setColor(pdf, this.colors.text, "text");
    rows.forEach(([label, value, bold]) => {
      pdf.setFont("helvetica", bold ? "bold" : "normal");
      pdf.text(label, labelX, y, { align: "right" });
      pdf.text(value, right, y, { align: "right" });
      y += 6;
    });

    y += 2;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    pdf.text(isCredit ? "Credit Balance:" : "Balance:", labelX, y, { align: "right" });
    pdf.text(`$${this.formatNumber(header.balance)}`, right, y, {
      align: "right",
    });

    this._invoiceTotalsY = y + 4;
  }

  addInvoiceFooter(pdf, header) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    let y = this._invoiceTotalsY || pdf.lastAutoTable.finalY + 40;

    const pageHeight = pdf.internal.pageSize.height;

    // Notes (from MEMO)
    if (!this.isFieldEmpty(header.memo)) {
      if (y + 20 > pageHeight - this.pageMargin) {
        pdf.addPage();
        y = this.pageMargin + 10;
      }
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(10);
      this.setColor(pdf, this.colors.text, "text");
      pdf.text("Notes:", left, y);
      y += 5;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      this.setColor(pdf, this.colors.configText, "text");
      const noteLines = pdf.splitTextToSize(
        this.cleanText(header.memo),
        right - left
      );
      pdf.text(noteLines, left, y);
      y += noteLines.length * 4.5 + 4;
    }

    // Customer signature line
    if (y + 12 > pageHeight - this.pageMargin) {
      pdf.addPage();
      y = this.pageMargin + 10;
    }
    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.line(left + 40, y + 6, left + 110, y + 6);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("Customer Signature", left, y + 6);

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.configText, "text");
    pdf.text("Thank You!", right, y + 6, { align: "right" });
  }

  // ---------------------------------------------------------------------------
  // Statement PDF (mirrors the EBMS "STATEMENT" report). Driven by the
  // /open-invoices payload: { statementDate, customer, invoices, totals }.
  // Finance charges are always 0 — printed as a dash, like the ERP report does
  // when nothing has been charged.
  // ---------------------------------------------------------------------------
  async exportStatement(statement) {
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
      import("jspdf"),
      import("jspdf-autotable"),
    ]);

    const customer = statement.customer || {};
    const invoices = Array.isArray(statement.invoices)
      ? statement.invoices
      : [];
    const totals = statement.totals || {};

    const pdf = new jsPDF("p", "mm", "a4");
    this.setupFonts(pdf);

    let y = 15;
    y = await this.addStatementMasthead(pdf, statement, customer, y);
    y = this.addStatementRemitBlock(pdf, customer, y);
    this.addStatementInvoices(pdf, invoices, y, autoTable);
    this.addStatementAging(pdf, totals);

    const account = String(customer.id || "statement").replace(/[^\w-]/g, "");
    pdf.save(`statement-${account}-${statement.statementDate || ""}.pdf`);
  }

  async addStatementMasthead(pdf, statement, customer, yPosition) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const center = pdf.internal.pageSize.width / 2;
    const bandCenter = yPosition + 8;

    let leftY = bandCenter + 1.5;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text(this.company.name, left, leftY);
    leftY += 5;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    this.setColor(pdf, this.colors.configText, "text");
    [
      ...this.company.lines,
      `Phone: ${this.company.phone}`,
      this.company.hst,
    ].forEach((line) => {
      pdf.text(line, left, leftY);
      leftY += 4;
    });

    try {
      const logo = await this.loadLogoAsBase64("/icons/logo-sm.svg");
      const w = 40;
      const h = (34 / 203) * w;
      pdf.addImage(logo, "PNG", center - w / 2, bandCenter - h / 2, w, h);
    } catch (e) {
      /* logo is optional */
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("STATEMENT", right, bandCenter + 3, { align: "right" });

    // Account Number / Statement Date / Page box
    const boxY = yPosition + 16;
    const boxH = 12;
    const boxW = 90;
    const boxX = right - boxW;
    const colW = boxW / 3;

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.rect(boxX, boxY, boxW, boxH);
    pdf.line(boxX, boxY + 5, boxX + boxW, boxY + 5);
    pdf.line(boxX + colW, boxY, boxX + colW, boxY + boxH);
    pdf.line(boxX + colW * 2, boxY, boxX + colW * 2, boxY + boxH);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    ["Account Number", "Statement Date", "Page"].forEach((label, i) => {
      pdf.text(label, boxX + colW * i + colW / 2, boxY + 3.5, {
        align: "center",
      });
    });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    const values = [
      String(customer.id || "-"),
      this.formatDateUS(statement.statementDate || new Date()),
      "1 of 1",
    ];
    values.forEach((value, i) => {
      pdf.text(value, boxX + colW * i + colW / 2, boxY + 9.5, {
        align: "center",
      });
    });

    return Math.max(leftY, boxY + boxH) + 8;
  }

  // "To:" address on the left, the remittance lines the dealer fills in by hand
  // on the right (Check Number / Amount / Date Paid) — as on the ERP report.
  addStatementRemitBlock(pdf, customer, yPosition) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("To:", left, yPosition);

    let lineY = yPosition + 6;
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.configText, "text");
    this.addressLines(customer).forEach((line) => {
      pdf.text(line, left, lineY);
      lineY += 4.5;
    });

    let remitY = yPosition + 2;
    const labelX = right - 70;
    ["Check Number", "Amount", "Date Paid"].forEach((label) => {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(9);
      this.setColor(pdf, this.colors.text, "text");
      pdf.text(label, labelX, remitY, { align: "right" });
      this.setColor(pdf, this.colors.border, "draw");
      pdf.setLineWidth(0.3);
      pdf.line(labelX + 4, remitY + 1, right, remitY + 1);
      remitY += 8;
    });

    return Math.max(lineY, remitY) + 6;
  }

  addStatementInvoices(pdf, invoices, yPosition, autoTable) {
    const headerRgb = this.hexToRgb(this.colors.headerBackground);
    const borderRgb = this.hexToRgb(this.colors.border);
    const textRgb = this.hexToRgb(this.colors.text);

    const body = invoices.map((inv) => [
      inv.invDate ? this.formatDateUS(inv.invDate) : "-",
      inv.invoice || "-",
      inv.poNo || "",
      inv.isOverdue && inv.dueDate
        ? `OVERDUE: ${this.formatDateUS(inv.dueDate)}`
        : inv.dueDate
        ? `Due: ${this.formatDateUS(inv.dueDate)}`
        : "",
      this.formatNumber(inv.amount),
      this.formatNumber(inv.amountDue),
    ]);

    autoTable(pdf, {
      startY: yPosition,
      head: [
        ["Date", "Invoice", "P.O. Number", "Description", "Amount", "Balance"],
      ],
      body: body.length ? body : [["", "", "", "No open invoices", "", ""]],
      theme: "grid",
      styles: {
        fontSize: 8,
        cellPadding: 1.8,
        valign: "middle",
        lineColor: [borderRgb.r, borderRgb.g, borderRgb.b],
        lineWidth: 0.2,
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        font: "helvetica",
      },
      headStyles: {
        fillColor: [headerRgb.r, headerRgb.g, headerRgb.b],
        fontStyle: "bold",
        halign: "center",
        textColor: [textRgb.r, textRgb.g, textRgb.b],
      },
      columnStyles: {
        0: { halign: "center", cellWidth: 22 },
        1: { halign: "center", cellWidth: 26 },
        2: { halign: "center", cellWidth: 26 },
        3: { halign: "left" },
        4: { halign: "right", cellWidth: 26 },
        5: { halign: "right", cellWidth: 26 },
      },
      margin: { left: this.pageMargin, right: this.pageMargin },
    });

    return pdf.lastAutoTable.finalY + 4;
  }

  // The ageing band, pinned to the bottom of the last page like the ERP report.
  addStatementAging(pdf, totals) {
    const left = this.pageMargin;
    const right = pdf.internal.pageSize.width - this.pageMargin;
    const width = right - left;
    const pageHeight = pdf.internal.pageSize.height;
    const bandH = 16;
    const y = pageHeight - this.pageMargin - bandH - 8;

    // 4 equal ageing columns + a wider "Please Pay This Amount". The ERP report
    // has a Finance Charges column here; we don't charge them, so it's dropped.
    const payW = width * 0.26;
    const columns = [
      ["Current", totals.current],
      ["Overdue 1 - 30", totals.overdue1_30],
      ["Overdue 31 - 60", totals.overdue31_60],
      ["Over 61 Days", totals.over61],
    ];
    const colW = (width - payW) / columns.length;

    this.setColor(pdf, this.colors.headerBackground, "fill");
    pdf.rect(left, y, width, 6, "F");

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.rect(left, y, width, bandH);
    pdf.line(left, y + 6, right, y + 6);
    columns.forEach((_, i) => {
      if (i === 0) return;
      pdf.line(left + colW * i, y, left + colW * i, y + bandH);
    });
    pdf.line(
      left + colW * columns.length,
      y,
      left + colW * columns.length,
      y + bandH
    );

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(8);
    this.setColor(pdf, this.colors.text, "text");
    columns.forEach(([label], i) => {
      pdf.text(label, left + colW * i + colW / 2, y + 4, { align: "center" });
    });
    pdf.text(
      "Please Pay This Amount",
      left + colW * columns.length + payW / 2,
      y + 4,
      { align: "center" }
    );

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    columns.forEach(([, value], i) => {
      const amount = Number(value) || 0;
      pdf.text(
        amount ? this.formatNumber(amount) : "-",
        left + colW * i + colW / 2,
        y + 12,
        { align: "center" }
      );
    });

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(10);
    pdf.text("TOTAL", left + colW * columns.length + 6, y + 12);
    pdf.text(this.formatNumber(totals.total), right - 6, y + 12, {
      align: "right",
    });

    pdf.setFont("helvetica", "italic");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.configText, "text");
    pdf.text("Please pay promptly!", right, y + bandH + 6, { align: "right" });
  }

  // Draws a single line made of mixed-weight words, right-aligned to `right`.
  // Words are joined with a normal space; only the segments flagged bold use
  // the bold font.
  drawRightAlignedSegments(pdf, segments, right, y) {
    const space = (() => {
      pdf.setFont("helvetica", "normal");
      return pdf.getTextWidth(" ");
    })();

    const measured = segments.map((s) => {
      pdf.setFont("helvetica", s.bold ? "bold" : "normal");
      return { ...s, width: pdf.getTextWidth(s.text) };
    });

    const total =
      measured.reduce((sum, s) => sum + s.width, 0) +
      space * Math.max(0, measured.length - 1);

    let x = right - total;
    measured.forEach((s, i) => {
      pdf.setFont("helvetica", s.bold ? "bold" : "normal");
      pdf.text(s.text, x, y);
      x += s.width + (i < measured.length - 1 ? space : 0);
    });
  }

  cleanText(value) {
    if (value === null || value === undefined) return "";
    return String(value)
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/&amp;/g, "&")
      .replace(/\s+/g, " ")
      .trim();
  }

  formatQty(value) {
    const n = parseFloat(value);
    if (isNaN(n) || n === 0) return "";
    return String(Math.round(n * 100) / 100);
  }

  formatNumber(amount) {
    const n = typeof amount === "string" ? parseFloat(amount) : amount;
    if (isNaN(n)) return "0.00";
    const parts = n.toFixed(2).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  }

  formatDateUS(value) {
    // Backend sends plain "YYYY-MM-DD"; parse the parts directly so the date
    // isn't shifted by the local timezone (new Date("YYYY-MM-DD") is UTC).
    if (typeof value === "string") {
      const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]}`;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return typeof value === "string" ? value : "-";
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  setupFonts(pdf) {
    pdf.setFont("helvetica", "normal");
  }

  hexToRgb(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
      : { r: 0, g: 0, b: 0 };
  }

  setColor(pdf, hexColor, type = "text") {
    const rgb = this.hexToRgb(hexColor);
    if (type === "text") {
      pdf.setTextColor(rgb.r, rgb.g, rgb.b);
    } else if (type === "draw") {
      pdf.setDrawColor(rgb.r, rgb.g, rgb.b);
    } else if (type === "fill") {
      pdf.setFillColor(rgb.r, rgb.g, rgb.b);
    }
  }

  async addLogo(pdf, yPosition) {
    try {
      const logoBase64 = await this.loadLogoAsBase64("/icons/logo-sm.svg");

      const logoWidth = 203 * 0.264583;
      const logoHeight = 34 * 0.264583;

      pdf.addImage(
        logoBase64,
        "PNG",
        this.pageMargin,
        yPosition,
        logoWidth,
        logoHeight
      );

      return yPosition + logoHeight + 5;
    } catch (error) {
      console.warn("Logo loading error:", error);
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");

      this.setColor(pdf, this.colors.text, "text");
      pdf.text("QUOTE", this.pageMargin, yPosition + 10);
      return yPosition + 20;
    }
  }

  async loadLogoAsBase64(url) {
    return new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error("No URL provided"));
        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          canvas.width = 168;
          canvas.height = 42;

          ctx.drawImage(img, 0, 0, 168, 42);

          const dataURL = canvas.toDataURL("image/png");
          resolve(dataURL);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error("Failed to load logo"));
      };

      const separator = url.includes("?") ? "&" : "?";
      img.src = `${url}${separator}_t=${Date.now()}`;
    });
  }

  isFieldEmpty(value) {
    return !value || (typeof value === "string" && value.trim() === "");
  }

  addQuoteInfo(pdf, quote, yPosition) {
    const rightMargin = pdf.internal.pageSize.width - this.pageMargin;

    this.setColor(pdf, this.colors.text, "text");

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.3);
    pdf.line(this.pageMargin, yPosition, rightMargin, yPosition);
    yPosition += 8;

    pdf.setFontSize(this.fontSize);
    pdf.setFont("helvetica", "bold");

    this.setColor(pdf, this.colors.text, "text");
    pdf.text("Quote Name:", this.pageMargin, yPosition);
    pdf.setFont("helvetica", "normal");

    const quoteName = quote.name || "Unnamed Quote";
    const maxNameWidth = rightMargin - (this.pageMargin + 30);
    const nameLines = pdf.splitTextToSize(quoteName, maxNameWidth);
    pdf.text(nameLines, this.pageMargin + 30, yPosition);
    yPosition += nameLines.length * 5 + 2;

    if (!this.isFieldEmpty(quote.description)) {
      pdf.setFont("helvetica", "bold");
      pdf.text("Description:", this.pageMargin, yPosition);
      pdf.setFont("helvetica", "normal");

      const maxWidth = rightMargin - (this.pageMargin + 30);
      const descriptionLines = pdf.splitTextToSize(quote.description, maxWidth);
      pdf.text(descriptionLines, this.pageMargin + 30, yPosition);
      yPosition += descriptionLines.length * 5 + 2;
    }

    pdf.setFont("helvetica", "bold");
    pdf.text("Created:", this.pageMargin, yPosition);
    pdf.setFont("helvetica", "normal");
    pdf.text(this.formatDate(quote.created), this.pageMargin + 30, yPosition);
    yPosition += 7;

    if (
      quote.user &&
      (quote.user.name || quote.user.email || quote.user.phone)
    ) {
      yPosition += 3;
      pdf.setFont("helvetica", "bold");
      pdf.text("Contact Information:", this.pageMargin, yPosition);
      yPosition += 5;

      pdf.setFont("helvetica", "normal");
      if (!this.isFieldEmpty(quote.user.name)) {
        pdf.text(`Name: ${quote.user.name}`, this.pageMargin + 5, yPosition);
        yPosition += 5;
      }
      if (!this.isFieldEmpty(quote.user.email)) {
        pdf.text(`Email: ${quote.user.email}`, this.pageMargin + 5, yPosition);
        yPosition += 5;
      }
      if (!this.isFieldEmpty(quote.user.phone)) {
        pdf.text(`Phone: ${quote.user.phone}`, this.pageMargin + 5, yPosition);
        yPosition += 5;
      }
      yPosition += 2;
    }

    yPosition += 3;
    this.setColor(pdf, this.colors.border, "draw");
    pdf.line(this.pageMargin, yPosition, rightMargin, yPosition);
    yPosition += 8;

    return yPosition;
  }

  getSelectedConfigurations(product) {
    if (product.options && Array.isArray(product.options)) {
      return product.options.map((option) => ({
        id: option.id,
        configName: option.name,
        name: option.value,
        quan: option.count ? parseFloat(option.count) : 1,
        quantity: option.count ? parseFloat(option.count) : 1,
        price: parseFloat(option.price) || 0,
        photo: option.photo || null,
      }));
    }

    if (
      product.selectedConfigurations &&
      Array.isArray(product.selectedConfigurations)
    ) {
      return product.selectedConfigurations.map((config) => ({
        id: config.id,
        configName: config.configName,
        name: config.name || config.value,
        quan: config.quan || config.quantity || 1,
        quantity: config.quan || config.quantity || 1,
        price: parseFloat(config.price) || 0,
        photo: config.photo || null,
      }));
    }

    if (
      product.formatedConfigurations &&
      Array.isArray(product.formatedConfigurations)
    ) {
      return product.formatedConfigurations.map((config) => ({
        id: config.id,
        configName: config.configName,
        name: config.name || config.value,
        quan: config.quan || config.quantity || 1,
        quantity: config.quan || config.quantity || 1,
        price: parseFloat(config.price) || 0,
        photo: config.photo || null,
      }));
    }

    if (product.configurations && Array.isArray(product.configurations)) {
      const defaultConfigs = [];

      product.configurations.forEach((configGroup) => {
        if (configGroup.items && configGroup.default) {
          const defaultItem = configGroup.items.find(
            (item) => item.id === configGroup.default
          );
          if (defaultItem) {
            defaultConfigs.push({
              id: defaultItem.id,
              configName: configGroup.name,
              name: defaultItem.name,
              quan: defaultItem.quan || 1,
              quantity: defaultItem.quan || 1,
              price: parseFloat(defaultItem.price) || 0,
              photo: defaultItem.photo || null,
            });
          }
        }
      });

      return defaultConfigs;
    }

    return [];
  }

  async addProductsTable(pdf, quote, yPosition, autoTable) {
    const products = quote.products;
    const pricingLabel = quote.pricingLabel || "Price";

    if (!products || products.length === 0) {
      pdf.setFont("helvetica", "normal");
      this.setColor(pdf, this.colors.configText, "text");
      pdf.text("No products found", this.pageMargin, yPosition);
      return;
    }

    const tableRows = [];
    const imagePromises = [];

    for (let i = 0; i < products.length; i++) {
      const product = products[i];

      let productQuantity = 1;

      if (product.count) {
        if (typeof product.count === "string") {
          productQuantity = parseFloat(product.count) || 1;
        } else {
          productQuantity = product.count;
        }
      } else if (product.quantity) {
        if (typeof product.quantity === "object" && product.quantity.__ob__) {
          productQuantity =
            Object.values(product.quantity).find(
              (val) => typeof val === "number"
            ) || 1;
        } else if (typeof product.quantity === "string") {
          productQuantity = parseFloat(product.quantity) || 1;
        } else {
          productQuantity = product.quantity;
        }
      }

      const mainRow = {
        image: "",
        name: product.name || "Unknown Product",
        unit: product.unit || "",
        quantity: String(Math.round(Number(productQuantity) * 100) / 100),
        price: this.formatCurrency(product.price || 0),
        isMainProduct: true,
        hasImage: !!product.photo,
        imageUrl: product.photo,
      };
      tableRows.push(mainRow);

      if (product.photo) {
        const rowIndex = tableRows.length - 1;
        imagePromises.push(
          this.loadImageAsBase64(product.photo)
            .then((base64) => ({ index: rowIndex, base64 }))
            .catch(() => null)
        );
      }

      const selectedConfigs = this.getSelectedConfigurations(product);

      selectedConfigs.forEach((config, configIndex) => {
        let optionText = "";
        if (config.configName && config.name) {
          optionText = `${config.configName}: ${config.name}`;
        } else if (config.name) {
          optionText = config.name;
        } else {
          optionText = "Configuration";
        }

        if (optionText.length > 55) {
          optionText = optionText.substring(0, 52) + "...";
        }

        const optionRow = {
          image: "",
          name: `    • ${optionText}`,
          unit: "",
          quantity: String(config.quan || config.quantity || 1),
          price: this.formatCurrency(parseFloat(config.price) || 0),
          isMainProduct: false,
          isOption: true,
          hasImage: !!config.photo,
          imageUrl: config.photo,
        };

        tableRows.push(optionRow);

        if (config.photo) {
          const rowIndex = tableRows.length - 1;
          imagePromises.push(
            this.loadImageAsBase64(config.photo)
              .then((base64) => ({ index: rowIndex, base64 }))
              .catch(() => null)
          );
        }
      });
    }

    const loadedImages = await Promise.all(imagePromises);
    const imageMap = {};
    loadedImages.forEach((img) => {
      if (img && img.base64) {
        imageMap[img.index] = img.base64;
      }
    });

    const bgRgb = this.hexToRgb(this.colors.background);
    const borderRgb = this.hexToRgb(this.colors.border);
    const headerRgb = this.hexToRgb(this.colors.headerBackground);
    const textRgb = this.hexToRgb(this.colors.text);
    const configTextRgb = this.hexToRgb(this.colors.configText);
    const configBgRgb = this.hexToRgb(this.colors.configBg);

    autoTable(pdf, {
      startY: yPosition,
      columns: [
        { header: "Image", dataKey: "image" },
        { header: "Product Name", dataKey: "name" },
        { header: "Unit", dataKey: "unit" },
        { header: "Q-ty", dataKey: "quantity" },
        { header: `${pricingLabel} Price`, dataKey: "price" },
      ],
      body: tableRows,
      theme: "grid",
      styles: {
        fontSize: 9,
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        fillColor: [bgRgb.r, bgRgb.g, bgRgb.b],
        lineColor: [borderRgb.r, borderRgb.g, borderRgb.b],
        lineWidth: 0.2,
        cellPadding: 3,
        overflow: "linebreak",
        cellWidth: "wrap",
        valign: "middle",
        font: "helvetica",
      },
      headStyles: {
        fillColor: [headerRgb.r, headerRgb.g, headerRgb.b],
        fontStyle: "bold",
        textColor: [textRgb.r, textRgb.g, textRgb.b],
        fontSize: 10,
        halign: "center",
      },
      columnStyles: {
        0: { cellWidth: 20, halign: "center" },
        1: { cellWidth: "auto" },
        2: { cellWidth: 25, halign: "center" },
        3: { cellWidth: 15, halign: "center" },
        4: { cellWidth: 25, halign: "right" },
      },
      alternateRowStyles: {
        fillColor: [bgRgb.r, bgRgb.g, bgRgb.b],
      },
      didDrawCell: (data) => {
        if (data.column.index === 0 && data.cell.section === "body") {
          const rowData = tableRows[data.row.index];

          if (rowData && (rowData.isMainProduct || rowData.isOption)) {
            if (imageMap[data.row.index]) {
              try {
                const maxImgSize = Math.min(
                  data.cell.width - 2,
                  data.cell.height - 2,
                  12
                );
                const x = data.cell.x + (data.cell.width - maxImgSize) / 2;
                const y = data.cell.y + (data.cell.height - maxImgSize) / 2;

                pdf.addImage(
                  imageMap[data.row.index],
                  "JPEG",
                  x,
                  y,
                  maxImgSize,
                  maxImgSize
                );
              } catch (error) {
                console.warn("Image loading error:", error);
                this.drawImagePlaceholder(pdf, data.cell);
              }
            } else {
              this.drawImagePlaceholder(pdf, data.cell);
            }
          }
        }
      },
      didParseCell: (data) => {
        if (data.cell.section === "body") {
          const rowData = tableRows[data.row.index];

          if (rowData && rowData.isOption) {
            data.cell.styles.fillColor = [
              configBgRgb.r,
              configBgRgb.g,
              configBgRgb.b,
            ];
            data.cell.styles.fontSize = 8;
            data.cell.styles.textColor = [
              configTextRgb.r,
              configTextRgb.g,
              configTextRgb.b,
            ];
            data.cell.styles.fontStyle = "normal";
          }

          if (rowData && rowData.isMainProduct) {
            data.cell.styles.fontStyle = "bold";
          }
        }
      },
      margin: { left: this.pageMargin, right: this.pageMargin },
    });

    this.addSummary(pdf, quote);
  }

  drawImagePlaceholder(pdf, cell) {
    const maxSize = Math.min(cell.width - 2, cell.height - 2, 12);
    const x = cell.x + (cell.width - maxSize) / 2;
    const y = cell.y + (cell.height - maxSize) / 2;

    this.setColor(pdf, "#F0F0F0", "fill");
    pdf.rect(x, y, maxSize, maxSize, "F");

    this.setColor(pdf, "#D0D0D0", "draw");
    pdf.setLineWidth(0.1);
    pdf.rect(x, y, maxSize, maxSize);

    pdf.setFontSize(5);
    this.setColor(pdf, "#999999", "text");
    pdf.text("no img", x + maxSize / 2, y + maxSize / 2 + 1, {
      align: "center",
    });
  }

  addSummary(pdf, quote) {
    const finalY = pdf.lastAutoTable.finalY || 100;
    const totalAmount = quote.total || 0;
    const pricingLabel = quote.pricingLabel || "Price";

    const rightMargin = pdf.internal.pageSize.width - this.pageMargin;

    // Unit handling fee: its own line above the total (which already includes it).
    let y = finalY;
    if (quote.handling > 0) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      this.setColor(pdf, this.colors.text, "text");
      pdf.text("Handling:", rightMargin - 65, y + 8);
      pdf.text(this.formatCurrency(quote.handling), rightMargin, y + 8, {
        align: "right",
      });
      y += 6;
    }

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.5);
    pdf.line(rightMargin - 65, y + 8, rightMargin, y + 8);

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text(`${pricingLabel} Total:`, rightMargin - 65, y + 16);
    pdf.text(this.formatCurrency(totalAmount), rightMargin, y + 16, {
      align: "right",
    });

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    this.setColor(pdf, this.colors.configText, "text");
    pdf.text("*Sales Tax not included", rightMargin, y + 23, {
      align: "right",
    });

    let currentY = y + 35;

    if (quote.shipping) {
      const shipping = quote.shipping;
      const hasShippingInfo =
        !this.isFieldEmpty(shipping.name) ||
        !this.isFieldEmpty(shipping.lastName) ||
        !this.isFieldEmpty(shipping.email) ||
        !this.isFieldEmpty(shipping.phone) ||
        !this.isFieldEmpty(shipping.address1) ||
        !this.isFieldEmpty(shipping.address2) ||
        !this.isFieldEmpty(shipping.city) ||
        !this.isFieldEmpty(shipping.state) ||
        !this.isFieldEmpty(shipping.postal) ||
        !this.isFieldEmpty(shipping.country) ||
        !this.isFieldEmpty(shipping.contact);

      if (hasShippingInfo) {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(11);
        this.setColor(pdf, this.colors.text, "text");
        pdf.text("Retailer Information:", this.pageMargin, currentY);
        currentY += 6;

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(10);

        if (
          !this.isFieldEmpty(shipping.name) ||
          !this.isFieldEmpty(shipping.lastName)
        ) {
          const fullName = [shipping.name, shipping.lastName]
            .filter((n) => !this.isFieldEmpty(n))
            .join(" ");
          if (fullName) {
            pdf.text(`Name: ${fullName}`, this.pageMargin, currentY);
            currentY += 5;
          }
        }

        if (!this.isFieldEmpty(shipping.email)) {
          pdf.text(`Email: ${shipping.email}`, this.pageMargin, currentY);
          currentY += 5;
        }

        if (!this.isFieldEmpty(shipping.phone)) {
          pdf.text(`Phone: ${shipping.phone}`, this.pageMargin, currentY);
          currentY += 5;
        }

        const addressParts = [];
        if (!this.isFieldEmpty(shipping.address1))
          addressParts.push(shipping.address1);
        if (!this.isFieldEmpty(shipping.address2))
          addressParts.push(shipping.address2);
        if (!this.isFieldEmpty(shipping.city)) addressParts.push(shipping.city);
        if (!this.isFieldEmpty(shipping.state))
          addressParts.push(shipping.state);
        if (!this.isFieldEmpty(shipping.postal))
          addressParts.push(shipping.postal);
        if (!this.isFieldEmpty(shipping.country))
          addressParts.push(shipping.country);

        if (addressParts.length > 0) {
          const fullAddress = addressParts.join(", ");
          const maxWidth = rightMargin - this.pageMargin;
          const addressLines = pdf.splitTextToSize(
            `Address: ${fullAddress}`,
            maxWidth
          );
          pdf.text(addressLines, this.pageMargin, currentY);
          currentY += addressLines.length * 5;
        }

        if (!this.isFieldEmpty(shipping.contact)) {
          pdf.text(`Contact: ${shipping.contact}`, this.pageMargin, currentY);
          currentY += 5;
        }

        currentY += 5;
      }
    }

    let notesY = currentY;

    const pageHeight = pdf.internal.pageSize.height;
    if (notesY + 40 > pageHeight - this.pageMargin) {
      pdf.addPage();
      notesY = this.pageMargin + 10;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(11);
    this.setColor(pdf, this.colors.text, "text");
    pdf.text("Notes:", this.pageMargin, notesY);

    notesY += 8;

    this.setColor(pdf, this.colors.border, "draw");
    pdf.setLineWidth(0.2);
    for (let i = 0; i < 5; i++) {
      pdf.line(this.pageMargin, notesY, rightMargin, notesY);
      notesY += 10;
    }
  }

  async loadImageAsBase64(url) {
    return new Promise((resolve, reject) => {
      if (!url) {
        reject(new Error("No URL provided"));
        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");

          const size = 120;
          canvas.width = size;
          canvas.height = size;

          const scale = Math.min(size / img.width, size / img.height);
          const width = img.width * scale;
          const height = img.height * scale;
          const x = (size - width) / 2;
          const y = (size - height) / 2;

          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, size, size);

          ctx.drawImage(img, x, y, width, height);

          const dataURL = canvas.toDataURL("image/jpeg", 0.7);
          resolve(dataURL);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        resolve(this.createImagePlaceholder());
      };

      const separator = url.includes("?") ? "&" : "?";
      img.src = `${url}${separator}_t=${Date.now()}`;
    });
  }

  createImagePlaceholder() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const size = 100;
    canvas.width = size;
    canvas.height = size;

    ctx.fillStyle = "#F0F0F0";
    ctx.fillRect(0, 0, size, size);

    ctx.strokeStyle = "#D0D0D0";
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, size, size);

    ctx.fillStyle = "#999999";
    ctx.font = "14px Arial";
    ctx.textAlign = "center";
    ctx.fillText("no img", size / 2, size / 2 + 4);

    return canvas.toDataURL("image/jpeg", 0.5);
  }

  formatDate(dateString) {
    try {
      if (!dateString) return "N/A";

      if (dateString.includes(".") && dateString.includes("(")) {
        const datePart = dateString.split(" ")[0];
        return datePart;
      }

      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}.${month}.${year}`;
    } catch (error) {
      return dateString || "N/A";
    }
  }

  formatCurrency(amount) {
    try {
      const numAmount =
        typeof amount === "string" ? parseFloat(amount) : amount;
      if (isNaN(numAmount)) return "$0.00";

      const formatted = numAmount.toFixed(2);

      const parts = formatted.split(".");
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");

      return `$${parts.join(".")}`;
    } catch (error) {
      return "$0.00";
    }
  }
}

export default ({ app }, inject) => {
  const pdfService = new PDFExportService();
  inject("pdfExport", pdfService);
};
